import { useState, useMemo, useRef, useCallback } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { Plane, Lock, AlertTriangle, Shield, MapPin, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PricingDialog from "@/components/PricingDialog";
import { supabase } from "@/integrations/supabase/client";

const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Major city coordinates — expanded with conflict zones
const CITY_COORDS: Record<string, [number, number]> = {
  // Europe
  rome: [41.9028, 12.4964], istanbul: [41.0082, 28.9784], london: [51.5074, -0.1278],
  paris: [48.8566, 2.3522], berlin: [52.52, 13.405], warsaw: [52.2297, 21.0122],
  vienna: [48.2082, 16.3738], madrid: [40.4168, -3.7038], athens: [37.9838, 23.7275],
  bucharest: [44.4268, 26.1025], sofia: [42.6977, 23.3219], belgrade: [44.7866, 20.4489],
  zagreb: [45.815, 15.9819], tbilisi: [41.7151, 44.8271], lisbon: [38.7223, -9.1393],
  helsinki: [60.1699, 24.9384], stockholm: [59.3293, 18.0686], oslo: [59.9139, 10.7522],
  prague: [50.0755, 14.4378], budapest: [47.4979, 19.0402], bratislava: [48.1486, 17.1077],
  // Ukraine
  kyiv: [50.4501, 30.5234], odesa: [46.4825, 30.7233], kharkiv: [49.9935, 36.2304],
  lviv: [49.8397, 24.0297], dnipro: [48.4647, 35.0462], zaporizhzhia: [47.8388, 35.1396],
  mariupol: [47.0958, 37.5483], kherson: [46.6354, 32.6169], donetsk: [48.0159, 37.8029],
  luhansk: [48.574, 39.3078],
  // Middle East
  jerusalem: [31.7683, 35.2137], "tel aviv": [32.0853, 34.7818], ankara: [39.9334, 32.8597],
  dubai: [25.2048, 55.2708], cairo: [30.0444, 31.2357], baghdad: [33.3152, 44.3661],
  tehran: [35.6892, 51.389], riyadh: [24.7136, 46.6753], beirut: [33.8938, 35.5018],
  amman: [31.9454, 35.9284], damascus: [33.5138, 36.2765], aleppo: [36.2021, 37.1343],
  sanaa: [15.3694, 44.191], aden: [12.7855, 45.0187], mosul: [36.34, 43.13],
  erbil: [36.1901, 44.0119], gaza: [31.5, 34.47], ramallah: [31.9038, 35.2034],
  // Africa — conflict zones
  tripoli: [32.8872, 13.1913], benghazi: [32.1194, 20.0868],
  khartoum: [15.5007, 32.5599], "port sudan": [19.6158, 37.2164],
  mogadishu: [2.0469, 45.3182], "addis ababa": [9.0054, 38.7636],
  nairobi: [1.2921, 36.8219], kinshasa: [4.4419, 15.2663], goma: [1.6585, 29.2203],
  bamako: [12.6392, -8.0029], niamey: [13.5127, 2.1128], ouagadougou: [12.3714, -1.5197],
  lagos: [6.5244, 3.3792], abuja: [9.0579, 7.4951], maiduguri: [11.8311, 13.151],
  // Asia
  moscow: [55.7558, 37.6173], tokyo: [35.6762, 139.6503], beijing: [39.9042, 116.4074],
  taipei: [25.033, 121.5654], seoul: [37.5665, 126.978], mumbai: [19.076, 72.8777],
  "hong kong": [22.3193, 114.1694], kabul: [34.5553, 69.2075], islamabad: [33.6844, 73.0479],
  "new delhi": [28.6139, 77.209], bangkok: [13.7563, 100.5018], manila: [14.5995, 120.9842],
  yangon: [16.8661, 96.1951], hanoi: [21.0285, 105.8542], singapore: [1.3521, 103.8198],
  pyongyang: [39.0392, 125.7625],
  // Americas
  "new york": [40.7128, -74.006], washington: [38.9072, -77.0369],
  "mexico city": [19.4326, -99.1332], bogota: [4.711, -74.0721], caracas: [10.4806, -66.9036],
  "buenos aires": [-34.6037, -58.3816], lima: [-12.0464, -77.0428],
  "sao paulo": [-23.5505, -46.6333],
};

const CITY_NAMES = Object.keys(CITY_COORDS).map(c =>
  c.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
);

// Cache for dynamically geocoded cities
const geocodeCache: Record<string, [number, number]> = {};

const geocodeCity = async (query: string): Promise<[number, number] | null> => {
  const key = query.toLowerCase().trim();
  if (CITY_COORDS[key]) return CITY_COORDS[key];
  if (geocodeCache[key]) return geocodeCache[key];
  try {
    const { data, error } = await supabase.functions.invoke("geocode", { body: { query } });
    if (error || !data?.found) return null;
    const coords: [number, number] = [data.lat, data.lng];
    geocodeCache[key] = coords;
    return coords;
  } catch {
    return null;
  }
};

const CityAutocomplete = ({ value, onChange, placeholder, label }: {
  value: string; onChange: (v: string) => void; placeholder: string; label: string;
}) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(() => {
    if (!value || value.length < 1) return [];
    const lower = value.toLowerCase();
    return CITY_NAMES.filter(c => c.toLowerCase().includes(lower)).slice(0, 8);
  }, [value]);

  return (
    <div className="relative">
      <label className="text-[10px] font-mono uppercase text-muted-foreground mb-1 block">{label}</label>
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="bg-background"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-md overflow-hidden max-h-48 overflow-y-auto">
          {suggestions.map((city) => (
            <button
              key={city}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(city); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
            >
              <MapPin className="inline h-3 w-3 mr-2 text-muted-foreground" />
              {city}
            </button>
          ))}
        </div>
      )}
      {open && value.length >= 2 && suggestions.length === 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-md p-2">
          <p className="text-xs text-muted-foreground">Not in list — will use dynamic geocoding</p>
        </div>
      )}
    </div>
  );
};

const TravelSafetyCheck = () => {
  const { isPremium } = useAuth();
  const { data: events = [] } = useEvents(undefined, isPremium);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [resolvedFrom, setResolvedFrom] = useState<[number, number] | null>(null);
  const [resolvedTo, setResolvedTo] = useState<[number, number] | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);

  const handleCheck = useCallback(async () => {
    setLoading(true);
    setGeoError(null);
    const [fc, tc] = await Promise.all([geocodeCity(from), geocodeCity(to)]);
    if (!fc || !tc) {
      setGeoError(`Could not find coordinates for ${!fc ? `"${from}"` : ""}${!fc && !tc ? " and " : ""}${!tc ? `"${to}"` : ""}. Try a different city name.`);
      setLoading(false);
      return;
    }
    setResolvedFrom(fc);
    setResolvedTo(tc);
    setChecked(true);
    setLoading(false);
  }, [from, to]);

  const result = useMemo(() => {
    if (!checked || !resolvedFrom || !resolvedTo) return null;
    const fromCoords = resolvedFrom;
    const toCoords = resolvedTo;

    const now = Date.now();
    const routeEvents = events.filter((e) => {
      if (!e.lat || !e.lng) return false;
      const age = now - new Date(e.occurred_at).getTime();
      if (age > 7 * 86400000) return false;
      // Check if event is within 200km of the route line
      const midLat = (fromCoords[0] + toCoords[0]) / 2;
      const midLng = (fromCoords[1] + toCoords[1]) / 2;
      const distToFrom = haversineDistance(e.lat, e.lng, fromCoords[0], fromCoords[1]);
      const distToTo = haversineDistance(e.lat, e.lng, toCoords[0], toCoords[1]);
      const distToMid = haversineDistance(e.lat, e.lng, midLat, midLng);
      return Math.min(distToFrom, distToTo, distToMid) < 200;
    });

    const missileCount = routeEvents.filter((e) => e.event_type === "missile").length;
    const airRaidCount = routeEvents.filter((e) => e.event_type === "air_raid").length;
    const totalSevere = missileCount + airRaidCount;

    let riskScore = Math.min(100, Math.round(Math.log2(routeEvents.length + 1) * 15 + totalSevere * 10));
    let riskLevel: "LOW" | "MODERATE" | "HIGH" | "EXTREME" = "LOW";
    if (riskScore >= 75) riskLevel = "EXTREME";
    else if (riskScore >= 50) riskLevel = "HIGH";
    else if (riskScore >= 25) riskLevel = "MODERATE";

    return { routeEvents, riskScore, riskLevel, missileCount, airRaidCount, fromCoords, toCoords };
  }, [checked, resolvedFrom, resolvedTo, events]);

  const riskColors: Record<string, string> = {
    LOW: "text-green-400 border-green-500/30 bg-green-500/10",
    MODERATE: "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    HIGH: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    EXTREME: "text-destructive border-destructive/30 bg-destructive/10",
  };

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 flex items-center gap-3">
          <Plane className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Travel Safety Check
          </h2>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground mb-4">
            Enter your departure and destination to get a real-time risk assessment of your route.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 mb-4">
            <CityAutocomplete
              value={from}
              onChange={(v) => { setFrom(v); setChecked(false); }}
              placeholder="e.g. Rome"
              label="From"
            />
            <CityAutocomplete
              value={to}
              onChange={(v) => { setTo(v); setChecked(false); }}
              placeholder="e.g. Istanbul"
              label="To"
            />
            <div className="flex items-end">
              <Button onClick={handleCheck} disabled={!from || !to || loading} className="w-full sm:w-auto gap-1.5">
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
                {loading ? "Checking..." : "Check Route"}
              </Button>
            </div>
          </div>

          {geoError && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 mb-3">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <p className="text-sm text-destructive">{geoError}</p>
            </div>
          )}

          {result && !("error" in result) && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={`text-sm font-mono font-bold px-3 py-1 ${riskColors[result.riskLevel]}`}>
                  {result.riskLevel} RISK
                </Badge>
                <span className="font-mono text-2xl font-bold text-foreground">{result.riskScore}/100</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-background p-3 text-center">
                  <p className="font-mono text-lg font-bold text-foreground">{result.routeEvents.length}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Events near route</p>
                </div>
                <div className="rounded-lg border border-border bg-background p-3 text-center">
                  <p className="font-mono text-lg font-bold text-destructive">{result.missileCount}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Missile events</p>
                </div>
                <div className="rounded-lg border border-border bg-background p-3 text-center">
                  <p className="font-mono text-lg font-bold text-orange-400">{result.airRaidCount}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Air raid alerts</p>
                </div>
              </div>

              {!isPremium && (
                <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-center">
                  <p className="text-xs text-muted-foreground">
                    <Lock className="mr-1 inline h-3 w-3" />
                    Full route analysis with event details and recommendations.{" "}
                    <button onClick={() => setPricingOpen(true)} className="text-primary font-semibold hover:underline">
                      Upgrade to Premium
                    </button>
                  </p>
                </div>
              )}

              {isPremium && result.routeEvents.length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {result.routeEvents.slice(0, 10).map((e) => (
                    <div key={e.id} className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs">
                      <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="font-mono uppercase text-muted-foreground">{e.event_type}</span>
                      <span className="truncate text-foreground">{e.title}</span>
                      <span className="ml-auto shrink-0 text-muted-foreground">{e.country}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default TravelSafetyCheck;
